import { canReadDomain } from '@/api/core/authorization';
import type { ServiceClient } from '@/api/http/client';
import type { OperatorSession } from '@/auth/session';
import { isUuid } from '@/lib/validation';
import type { PropertyRuntime } from '../property-queries';
export interface Attempt {
  body: unknown;
  actor: string;
  accepted: boolean;
}
export interface Owner {
  attempt: Attempt | null;
  busy: boolean;
  epoch: string;
  version: number;
  readGeneration: number;
  listeners: Set<() => void>;
}
const owners = new WeakMap<OperatorSession, WeakMap<ServiceClient, Map<string, Owner>>>();
export const epoch = (r: PropertyRuntime) => {
  const s = r.session.getSnapshot();
  return JSON.stringify([s.status, s.generation, s.authorization, s.deniedDomains]);
};
/** 권한 영역은 호출한 업무가 정한다(기본 property). 다른 영역의 권한 비트를 property로 바꿔 읽지 않는다. */
export function allowed(r: PropertyRuntime, bits: number, domain = 'property') {
  const s = r.session.getSnapshot();
  return (
    s.status === 'authorized' &&
    !s.deniedDomains.includes(domain) &&
    !!s.authorization &&
    s.authorization.assigned &&
    (s.authorization.role_bits & (4 | 8)) !== 0 &&
    canReadDomain(s.authorization, domain, bits)
  );
}
export function publish(o: Owner) {
  o.version++;
  o.listeners.forEach((l) => {
    l();
  });
}
export function commandOwner(r: PropertyRuntime, scope: string): Owner {
  let clients = owners.get(r.session);
  if (!clients) {
    clients = new WeakMap();
    owners.set(r.session, clients);
  }
  let map = clients.get(r.client);
  if (!map) {
    map = new Map();
    clients.set(r.client, map);
  }
  const key = `${r.apiNamespace}:${scope}`;
  let o = map.get(key);
  if (!o) {
    o = {
      attempt: null,
      busy: false,
      epoch: epoch(r),
      version: 0,
      readGeneration: 0,
      listeners: new Set(),
    };
    map.set(key, o);
    const watched = o;
    r.session.subscribe(() => {
      const next = epoch(r);
      if (next !== watched.epoch) {
        watched.epoch = next;
        watched.readGeneration++;
        watched.attempt = null;
        watched.busy = false;
        publish(watched);
      }
    });
  }
  return o;
}
// 같은 순간에 여러 곳이 작업자를 물으면 요청 하나를 나눠 쓴다(개발 모드의 effect 이중 실행 등).
// 읽기 앞뒤 확인처럼 시간차를 두고 부르는 호출은 겹치지 않으므로 그대로 각각 서버에 간다.
const meInFlight = new WeakMap<ServiceClient, Promise<unknown>>();
function currentUser(client: ServiceClient) {
  const running = meInFlight.get(client);
  if (running) return running;
  const request = client.get<unknown>('users/me').finally(() => {
    if (meInFlight.get(client) === request) meInFlight.delete(client);
  });
  meInFlight.set(client, request);
  return request;
}
export async function actor(r: PropertyRuntime, bits: number, domain = 'property') {
  if (!allowed(r, bits, domain)) throw new Error('권한을 다시 확인해 주세요.');
  const v = await currentUser(r.client);
  if (
    !allowed(r, bits, domain) ||
    !v ||
    typeof v !== 'object' ||
    !('user_id' in v) ||
    !isUuid(v.user_id)
  )
    throw new Error('현재 작업자를 확인하지 못했습니다.');
  return v.user_id;
}

export function freezeRequest<T>(value: T): T {
  if (value && typeof value === 'object') {
    Object.values(value).forEach((child) => {
      freezeRequest(child);
    });
    Object.freeze(value);
  }
  return value;
}
